/****************
 * WHAT THE TRADES TABLE IS ACTUALLY SHOWING.
 *
 * Guillaume, GB-3, 2026-09-20: the Today / Week / Month selector on Best
 * Trades "has a lot of rendering issues and does not always work".
 *
 * ── WHY IT LOOKS BROKEN ────────────────────────────────────────────────────
 *
 * useTopTrades is three query keys over one table, and it carries
 * `placeholderData: keepPreviousData`. That is the right setting -- it stops
 * the table flashing empty between windows -- but the call site then rendered
 * the placeholder with no sign that it was one. Click "Today" and the WEEK's
 * rows stay on screen, unchanged, under a button that now says Today. If the
 * new window is slow, the selector looks like it did nothing. If it fails,
 * the old rows stay for ever and the selector looks broken.
 *
 * isError was discarded entirely, which is the same class of lie the Dashboard
 * already fixed for sessions: "a query still in flight -- or one that had
 * failed outright -- rendered +$0.00 and 0, which reads as 'you have no
 * sessions'." A failed window read must not render as "no trades this month".
 *
 * And an empty result rendered as nothing at all: no row, no message, just a
 * header over blank space, which reads as a broken page rather than a quiet
 * month.
 *
 * ── THE SIGNAL ─────────────────────────────────────────────────────────────
 *
 * `isPlaceholderData`, not `isFetching`. The query refetches every 30 seconds,
 * so isFetching is true on a schedule and blanking the table each time would
 * be a worse flicker than the one being fixed. isPlaceholderData is true in
 * exactly the case that matters: what is in hand belongs to a DIFFERENT window
 * than the one selected.
 ****************/

import i18n from '@/i18n';

export type TradeTableState = 'rows' | 'switching' | 'failed' | 'empty';

export interface TradeTableInput {
  /** Rows currently in hand -- which may belong to the previous window. */
  rowCount: number;
  /** The query failed and there is nothing trustworthy to show. */
  isError: boolean;
  /** What is in hand is the previous window's data, not this one's. */
  isPlaceholder: boolean;
}

export function tradeTableState(input: TradeTableInput): TradeTableState {
  /*
   * Order matters. A failed read is reported even when rows are in hand,
   * because those rows are the OLD window's -- showing them under the new
   * label is the defect, not the fallback.
   */
  if (input.isError) return 'failed';
  if (input.isPlaceholder) return 'switching';
  if (input.rowCount === 0) return 'empty';
  return 'rows';
}

/** What the single full-width row says when there is no table to draw. */
export function tradeTableMessage(state: TradeTableState, windowLabel: string): string | null {
  switch (state) {
    case 'switching':
      return i18n.t('messages:tradeTable.loading', { window: windowLabel.toLowerCase() });
    case 'failed':
      return i18n.t('messages:tradeTable.failed');
    case 'empty':
      return i18n.t('messages:tradeTable.empty', { window: windowLabel.toLowerCase() });
    default:
      return null;
  }
}
